import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import api from "../../utils/api";

const steps = ["Work Order Info", "Details & File", "Review & Submit"];

const AddWorkOrderSmart = ({
  propertyId,
  openedByUserId = null,   // 👈 NEW
  uploadedBy = 1,           // 👈 NEW (optional, used for file attribution)
}) => {
  const [activeStep, setActiveStep] = useState(0);

  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);

  const [formData, setFormData] = useState(() => {
    const draft = localStorage.getItem("work_order_draft");
    const parsedDraft = draft ? JSON.parse(draft) : {};
    return {
      description: parsedDraft.description || "",
      details: parsedDraft.details || "",
      property_id: propertyId || parsedDraft.property_id || "",
      workorder_type_id: parsedDraft.workorder_type_id || "",
      workorder_subtype_id: parsedDraft.workorder_subtype_id || "",
      location_id: parsedDraft.location_id || "",
    };
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 🔎 Debug the opener id
  useEffect(() => {
    
  }, [openedByUserId]);

  // Save draft locally
  useEffect(() => {
    localStorage.setItem("work_order_draft", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    if (!propertyId) return;
    setFormData((prev) => ({ ...prev, property_id: propertyId }));
  }, [propertyId]);

  // Fetch work order types
  useEffect(() => {
    const fetchTypes = async () => {
      if (!formData.property_id && !propertyId) return;
      try {
        const res = await api.get("/workorder-types/by-property", {
          params: { property_id: formData.property_id || propertyId },
        });
        setWorkOrderTypes(res.data?.types ?? []);
        if (!res.data?.types?.length) {
          setSnackbar({
            open: true,
            severity: "info",
            message:
              "No work order types available for this property. Try a different property or contact admin.",
          });
        }
      } catch (err) {
        console.error("Failed to fetch work order types:", err);
        setWorkOrderTypes([]);
        setSnackbar({
          open: true,
          severity: "error",
          message:
            err.response?.data?.message ||
            "Could not load work order types for this property.",
        });
      }
    };
    fetchTypes();
  }, [formData.property_id, propertyId]);

  // Fetch subtypes & locations when type changes
  useEffect(() => {
    if (!formData.workorder_type_id) {
      setSubtypes([]);
      setLocations([]);
      return;
    }

    const fetchSubtypes = async () => {
      try {
        const res = await api.get("/workorder-subtypes-by-type", {
          params: { type_id: formData.workorder_type_id },
        });
        setSubtypes(res.data);
      } catch (err) {
        console.error("Failed to fetch subtypes:", err);
        setSubtypes([]);
      }
    };

    const fetchLocations = async () => {
      try {
        const res = await api.get("/workorder-locations-by-type", {
          params: { type_id: formData.workorder_type_id },
        });
        setLocations(res.data);
      } catch (err) {
        console.error("Failed to fetch locations:", err);
        setLocations([]);
      }
    };

    fetchSubtypes();
    fetchLocations();

    setFormData((prev) => ({
      ...prev,
      workorder_subtype_id: "",
      location_id: "",
    }));
  }, [formData.workorder_type_id]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setSelectedFile(file);
  }, []);

  const removeFile = () => setSelectedFile(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*, .pdf, .doc, .docx",
    multiple: false,
  });

  const nextStep = () => setActiveStep((s) => s + 1);
  const prevStep = () => setActiveStep((s) => s - 1);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Build JSON payload for /addWorkOrder
      const payloadJSON = {
        property_id: formData.property_id,
        description: (formData.description || "").trim(),
        details: (formData.details || "").trim(),
        workorder_type_id: formData.workorder_type_id,
        workorder_subtype_id: formData.workorder_subtype_id || null,
        location_id: formData.location_id || null,
        opened_by_user_id: Number.isFinite(Number(openedByUserId))
          ? Number(openedByUserId)
          : null, // 👈 NEW
      };

      const createRes = await api.post("/addWorkOrder", payloadJSON);
      const workorder_id = createRes.data?.workOrderId;
      if (!workorder_id) {
        throw new Error(
          "Missing workOrderId in response: " + JSON.stringify(createRes.data)
        );
      }

      // If file attached, upload it
      if (selectedFile) {
        const uploadData = new FormData();
        uploadData.append("workorder_id", workorder_id);
        uploadData.append("upload_urls", selectedFile);
        uploadData.append(
          "uploaded_by",
          Number.isFinite(Number(uploadedBy)) ? Number(uploadedBy) : 1 // 👈 NEW (optional)
        );

        await api.post("/workorder-files", uploadData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // Reset
      setFormData({
        description: "",
        details: "",
        property_id: propertyId || "",
        workorder_type_id: "",
        workorder_subtype_id: "",
        location_id: "",
      });
      setSelectedFile(null);
      setActiveStep(0);
      localStorage.removeItem("work_order_draft");

      setSnackbar({
        open: true,
        message: "✅ Work order created successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Submit error (AddWorkOrderSmart):", err);
      setSnackbar({
        open: true,
        message:
          err.response?.data?.message || err.message || "Submission failed",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Tooltip title="Select the type of work order">
                <InputLabel sx={{ backgroundColor: "white", px: 0.5 }}>
                  Work Order Type
                </InputLabel>
              </Tooltip>
              <Select
                name="workorder_type_id"
                value={formData.workorder_type_id}
                onChange={handleFormChange}
                required
                sx={{ py: 1.5, fontSize: 14 }}
              >
                {workOrderTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }} disabled={!subtypes.length}>
              <InputLabel sx={{ backgroundColor: "white", px: 0.5 }}>
                Subtype
              </InputLabel>
              <Select
                name="workorder_subtype_id"
                value={formData.workorder_subtype_id}
                onChange={handleFormChange}
                required
                sx={{ py: 1.5, fontSize: 14 }}
              >
                {subtypes.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.subtype_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }} disabled={!locations.length}>
              <InputLabel sx={{ backgroundColor: "white", px: 0.5 }}>
                Location
              </InputLabel>
              <Select
                name="location_id"
                value={formData.location_id}
                onChange={handleFormChange}
                required
                sx={{ py: 1.5, fontSize: 14 }}
              >
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.location_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Property ID"
              name="property_id"
              value={formData.property_id}
              disabled
              sx={{ mb: 2 }}
            />
          </>
        );

      case 1:
        return (
          <>
            <Tooltip title="Provide a brief description of the work to be done">
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={2}
                sx={{ mb: 2 }}
                required
              />
            </Tooltip>

            <TextField
              fullWidth
              label="Details"
              name="details"
              value={formData.details}
              onChange={handleFormChange}
              multiline
              rows={3}
              sx={{ mb: 2 }}
              required
            />

            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #ccc",
                borderRadius: "8px",
                p: 3,
                mb: 2,
                cursor: "pointer",
                textAlign: "center",
                backgroundColor: isDragActive ? "#f9f9f9" : "#fafafa",
              }}
            >
              <input {...getInputProps()} />
              <Typography variant="body2">
                {isDragActive
                  ? "Drop a file here..."
                  : "Drag & drop an image or document, or click to select"}
              </Typography>
            </Box>

            {selectedFile && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="body2">{selectedFile.name}</Typography>
                <IconButton color="error" onClick={removeFile}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </>
        );

      case 2:
        return (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6">Review Your Work Order</Typography>
              <Typography>
                <b>Type:</b>{" "}
                {workOrderTypes.find((t) => t.id === formData.workorder_type_id)?.type}
              </Typography>
              <Typography>
                <b>Subtype:</b>{" "}
                {subtypes.find((s) => s.id === formData.workorder_subtype_id)?.subtype_name}
              </Typography>
              <Typography>
                <b>Location:</b>{" "}
                {locations.find((l) => l.id === formData.location_id)?.location_name}
              </Typography>
              <Typography>
                <b>Description:</b> {formData.description}
              </Typography>
              <Typography>
                <b>Details:</b> {formData.details}
              </Typography>
              <Typography>
                <b>File:</b> {selectedFile?.name || "None"}
              </Typography>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 4 }}>
      {/* Optional hidden input to verify in DOM/DevTools */}
      <input type="hidden" name="opened_by_user_id" value={openedByUserId ?? ""} />

      <Typography variant="h5" fontWeight="bold" mb={3}>
        🛠 Create New Work Order
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent()}

      <Box mt={4} display="flex" justifyContent="space-between">
        {activeStep > 0 && (
          <Button onClick={prevStep} disabled={isSubmitting}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={nextStep}
            disabled={
              isSubmitting || (activeStep === 0 && !formData.workorder_type_id)
            }
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Submit Work Order"
            )}
          </Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddWorkOrderSmart;
